import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";
import type {
  ImportEntityType,
  ImportJobListItem,
  ImportJobStatus,
} from "@adva/types";
import { useImportJobs } from "@/hooks/use-import";
import { PageHeader } from "@/components/v2/page-header";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { StatusBadge } from "@/components/status-badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  IMPORT_STATUS_LABELS,
  RECENT_STATUSES,
  type ImportCounterCell,
  type ImportJobSeverity,
  bucketImportJobs,
  formatEntityType,
  formatRelativeTime,
  importCounterCells,
  importJobSeverity,
  summarizeImportOutcome,
} from "../labels";
import { JobDetailSheet } from "./job-detail-sheet";
import { BatchDiagnosisPanel } from "./batch-diagnosis-panel";

/** Find the most recent batch with at least one errored / awaiting-review
 * child. Returns the batch_id of the newest qualifying batch, or null when
 * none exists in the visible window. Deduped on batch_id so a batch with
 * multiple errored children renders one panel, not N. (ADV-1018 Phase 3.) */
function pickRecentErroredBatch(jobs: ImportJobListItem[]): string | null {
  const ERROR_STATUSES: ReadonlySet<ImportJobStatus> = new Set([
    "failed",
    "completed_with_errors",
    "partially_approved",
    "awaiting_review",
  ] as ImportJobStatus[]);
  // The list endpoint still returns jobs created_at desc, and this walks that
  // raw `jobs` array (newest-first), so it returns the most recent qualifying
  // batch. The Recent *table* re-sorts client-side (ADV-1405) — that does not
  // affect `jobs`, so this stays correct.
  for (const j of jobs) {
    if (!j.batch_id) continue;
    if (ERROR_STATUSES.has(j.status) || (j.error_rows ?? 0) > 0) {
      return j.batch_id;
    }
  }
  return null;
}

const POLL_INTERVAL_MS = 5_000;
const FETCH_LIMIT = 100;

interface Breadcrumb {
  label: string;
  href?: string;
}

interface ImportDashboardProps {
  breadcrumbs: Breadcrumb[];
  title: string;
  subtitle?: string;
  /** Slot for the New Import action (button or trigger). */
  actions: React.ReactNode;
  /** Where Review-now buttons link. Defaults to /v2/import/jobs. */
  reviewBaseHref?: string;
  /** Empty-state CTA. Receives a click handler that should open the wizard. */
  emptyAction?: { label: string; onClick: () => void };
}

// ADV-1113 §2 (Option A): render all five counters in a fixed order with zeros
// kept but muted, so `created` (etc.) lines up vertically across rows. The
// fixed per-cell min-width keeps the columns aligned regardless of digit count.
function CounterLine({ cells }: { cells: ImportCounterCell[] }) {
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-3 tabular-nums">
      {cells.map((c) => (
        <span
          key={c.key}
          className={
            c.muted
              ? "text-[color:var(--color-text-lo)]"
              : "text-[color:var(--color-text-md)]"
          }
        >
          {c.value.toLocaleString()} {c.label}
        </span>
      ))}
    </span>
  );
}

function ResultsSummary({ row }: { row: ImportJobListItem }) {
  // ADV-1017: counts are now their own table columns; this cell shows
  // only the plain-English headline.
  const { headline } = summarizeImportOutcome(row);
  return (
    <span className="typo-body text-[color:var(--color-text-hi)]">
      {headline}
    </span>
  );
}

const SEVERITY_BG: Record<ImportJobSeverity, string> = {
  success: "bg-[color:var(--color-success)]",
  warning: "bg-[color:var(--color-warning)]",
  danger: "bg-[color:var(--color-danger)]",
};

function SeverityDot({ severity }: { severity: ImportJobSeverity }) {
  return (
    <span
      data-testid="severity-dot"
      data-severity={severity}
      className={`size-2 shrink-0 rounded-full ${SEVERITY_BG[severity]}`}
      aria-label={`Severity: ${severity}`}
    />
  );
}

function NeedsAttentionRow({
  job,
  reviewBaseHref,
}: {
  job: ImportJobListItem;
  reviewBaseHref: string;
}) {
  // ADV-1108: lead with a plain-English outcome (e.g. "26,745 rows held —
  // parents not imported yet") + compact counter line, so the user can
  // decide whether to click in without opening the detail screen.
  // ADV-1017: the table-row CounterLine has gone away, but this callout
  // surface still wants the full five-counter line, so we call the helper
  // directly instead of going through `summarizeImportOutcome`.
  const { headline } = summarizeImportOutcome(job);
  const counters = importCounterCells(job);

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className="size-2 shrink-0 rounded-full bg-[color:var(--color-warning)]"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <StatusBadge
            status={job.status}
            label={IMPORT_STATUS_LABELS[job.status]}
          />
          {/* ADV-1113 §3: entity type is the primary identity; source is a
              secondary chip so the user can tell what was uploaded, not just
              where it came from. */}
          <span className="typo-body font-medium text-[color:var(--color-text-hi)]">
            {formatEntityType(job.entity_type)}
          </span>
          {job.source && (
            <span className="typo-body text-[color:var(--color-text-md)]">
              {job.source}
            </span>
          )}
          <span className="typo-body text-[color:var(--color-text-md)]">
            {job.total_rows.toLocaleString()} rows
          </span>
        </div>
        <p className="typo-body mt-1 text-[color:var(--color-text-hi)]">
          {headline}
        </p>
        <p className="typo-label mt-0.5 normal-case tracking-normal">
          <CounterLine cells={counters} />
          <span className="text-[color:var(--color-text-md)]">
            {" • "}uploaded {formatRelativeTime(job.created_at)}
          </span>
        </p>
      </div>
      <Button asChild size="sm" variant="outline">
        <Link to={`${reviewBaseHref}/${job.id}`}>Review now →</Link>
      </Button>
    </div>
  );
}

function InProgressRow({ job }: { job: ImportJobListItem }) {
  const phase = job.normalization_status
    ? `${job.normalization_status.replace(/_/g, " ")}…`
    : IMPORT_STATUS_LABELS[job.status];

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Loader2
        className="size-4 shrink-0 animate-spin text-[color:var(--color-accent)]"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <StatusBadge
            status={job.status}
            label={IMPORT_STATUS_LABELS[job.status]}
          />
          <span className="typo-body font-medium text-[color:var(--color-text-hi)]">
            {job.source || formatEntityType(job.entity_type)}
          </span>
          <span className="typo-body text-[color:var(--color-text-md)]">
            {job.total_rows.toLocaleString()} records
          </span>
        </div>
        <p className="typo-label mt-1 text-[color:var(--color-text-md)] normal-case tracking-normal">
          Uploaded {formatRelativeTime(job.created_at)} • {phase}
        </p>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  count,
  action,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-baseline justify-between">
      <h2 className="typo-label text-[color:var(--color-text-md)]">
        {title}
        {typeof count === "number" && (
          <span className="ml-2 text-[color:var(--color-text-lo)]">
            ({count})
          </span>
        )}
      </h2>
      {action}
    </div>
  );
}

function CardList({ children }: { children: React.ReactNode }) {
  return (
    <Card className="divide-y divide-[color:var(--color-border)] p-0">
      {children}
    </Card>
  );
}

const RECENT_STATUS_OPTIONS: {
  value: "all" | ImportJobStatus;
  label: string;
}[] = [
  { value: "all", label: "All statuses" },
  ...RECENT_STATUSES.map((s) => ({
    value: s,
    label: IMPORT_STATUS_LABELS[s],
  })),
];

export function ImportDashboard({
  breadcrumbs,
  title,
  subtitle,
  actions,
  reviewBaseHref = "/import/jobs",
  emptyAction,
}: ImportDashboardProps) {
  const navigate = useNavigate();
  const [recentStatus, setRecentStatus] = useState<"all" | ImportJobStatus>(
    "all",
  );
  const [recentEntityType, setRecentEntityType] = useState<
    "all" | ImportEntityType
  >("all");
  const [sorting, setSorting] = useState<SortingState>([
    // ADV-1405: oldest-first so users work the import queue top-to-bottom.
    { id: "created_at", desc: false },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const [detailJobId, setDetailJobId] = useState<string | null>(null);
  const pageSize = 25;

  const { data, isLoading } = useImportJobs(
    { limit: FETCH_LIMIT },
    { refetchInterval: POLL_INTERVAL_MS },
  );

  const jobs = useMemo(() => data?.jobs ?? [], [data?.jobs]);
  const { attention, inProgress, recent } = useMemo(
    () => bucketImportJobs(jobs),
    [jobs],
  );
  // ADV-1018: surface a single "What happened?" panel for the most recent
  // batch that ended in errors / awaiting review. Computed off the same job
  // list so it stays in sync with the visible table; null = no recent batch.
  const erroredBatchId = useMemo(() => pickRecentErroredBatch(jobs), [jobs]);

  const entityTypeOptions = useMemo(() => {
    const set = new Set<ImportEntityType>();
    for (const j of recent) set.add(j.entity_type);
    return Array.from(set).sort();
  }, [recent]);

  const filteredRecent = useMemo(() => {
    return recent.filter((j) => {
      if (recentStatus !== "all" && j.status !== recentStatus) return false;
      if (recentEntityType !== "all" && j.entity_type !== recentEntityType)
        return false;
      return true;
    });
  }, [recent, recentStatus, recentEntityType]);

  // ADV-1405: DataTable is in controlled (manual) sorting mode, so it does NOT
  // re-order rows itself — the caller must pre-sort. Sort the already-fetched,
  // already-filtered set by created_at per the active sort state. This is row
  // ordering, not a business-metric aggregation, so it is allowed client-side.
  const sortedRecent = useMemo(() => {
    const col = sorting[0];
    if (!col || col.id !== "created_at") return filteredRecent;
    return [...filteredRecent].sort((a, b) => {
      const cmp = (a.created_at ?? "").localeCompare(b.created_at ?? "");
      return col.desc ? -cmp : cmp;
    });
  }, [filteredRecent, sorting]);

  const recentColumns: ColumnDef<ImportJobListItem, unknown>[] = useMemo(
    () => [
      {
        id: "severity",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <SeverityDot severity={importJobSeverity(row.original)} />
        ),
      },
      {
        accessorKey: "entity_type",
        header: "Entity",
        cell: ({ row }) => (
          <span className="font-medium">
            {formatEntityType(row.original.entity_type)}
          </span>
        ),
      },
      {
        accessorKey: "source",
        header: "Source",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-[color:var(--color-text-md)]">
            {row.original.source || "—"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.status}
            label={IMPORT_STATUS_LABELS[row.original.status]}
          />
        ),
      },
      {
        accessorKey: "total_rows",
        header: "Rows",
        meta: { numeric: true },
        cell: ({ row }) => row.original.total_rows.toLocaleString(),
      },
      {
        accessorKey: "created_count",
        header: "Created",
        meta: { numeric: true },
        cell: ({ row }) => (
          <span
            className={
              row.original.created_count > 0
                ? undefined
                : "text-[color:var(--color-text-lo)]"
            }
          >
            {row.original.created_count.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "updated_count",
        header: "Updated",
        meta: { numeric: true },
        cell: ({ row }) => (
          <span
            className={
              row.original.updated_count > 0
                ? undefined
                : "text-[color:var(--color-text-lo)]"
            }
          >
            {row.original.updated_count.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "error_rows",
        header: "Errors",
        meta: { numeric: true },
        cell: ({ row }) => (
          <span
            data-testid={`errors-cell-${row.original.id}`}
            className={
              row.original.error_rows > 0
                ? "text-[color:var(--color-danger)]"
                : "text-[color:var(--color-text-lo)]"
            }
          >
            {row.original.error_rows.toLocaleString()}
          </span>
        ),
      },
      {
        id: "results",
        header: "Results",
        enableSorting: false,
        cell: ({ row }) => <ResultsSummary row={row.original} />,
      },
      {
        accessorKey: "created_at",
        header: "Started",
        cell: ({ row }) => (
          <span className="text-[color:var(--color-text-md)]">
            {formatRelativeTime(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  const totalImports = jobs.length;
  const subtitleText =
    subtitle ??
    (isLoading
      ? "Loading recent imports…"
      : `${totalImports.toLocaleString()} job${totalImports === 1 ? "" : "s"} in the last ${FETCH_LIMIT}`);

  if (!isLoading && jobs.length === 0) {
    return (
      <>
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={title}
          subtitle={subtitleText}
          actions={actions}
        />
        <EmptyState
          title="No imports yet"
          description="Use the New import button to upload your first CSV."
          action={emptyAction}
        />
      </>
    );
  }

  function handleRecentRowClick(job: ImportJobListItem) {
    if (
      job.status === "awaiting_review" ||
      job.status === "expired" ||
      job.status === "partially_approved"
    ) {
      navigate(`${reviewBaseHref}/${job.id}`);
      return;
    }
    setDetailJobId(job.id);
  }

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={title}
        subtitle={subtitleText}
        actions={actions}
      />

      {attention.length > 0 && (
        <section className="mb-6" aria-label="Needs review">
          <SectionHeader title="Needs review" count={attention.length} />
          <CardList>
            {attention.map((job) => (
              <NeedsAttentionRow
                key={job.id}
                job={job}
                reviewBaseHref={reviewBaseHref}
              />
            ))}
          </CardList>
        </section>
      )}

      {inProgress.length > 0 && (
        <section className="mb-6" aria-label="In Progress">
          <SectionHeader title="In Progress" count={inProgress.length} />
          <CardList>
            {inProgress.map((job) => (
              <InProgressRow key={job.id} job={job} />
            ))}
          </CardList>
        </section>
      )}

      {erroredBatchId && (
        <section className="mb-6" aria-label="Batch analysis">
          <BatchDiagnosisPanel batchId={erroredBatchId} autoFire={false} />
        </section>
      )}

      <section aria-label="Recent imports">
        <SectionHeader
          title="Recent"
          count={filteredRecent.length}
          action={
            <div className="flex items-center gap-2">
              <Select
                value={recentEntityType}
                onValueChange={(v) =>
                  setRecentEntityType(v as "all" | ImportEntityType)
                }
              >
                <SelectTrigger className="h-8 w-[180px]">
                  <SelectValue placeholder="Entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All entity types</SelectItem>
                  {entityTypeOptions.map((et) => (
                    <SelectItem key={et} value={et}>
                      {formatEntityType(et)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={recentStatus}
                onValueChange={(v) =>
                  setRecentStatus(v as "all" | ImportJobStatus)
                }
              >
                <SelectTrigger className="h-8 w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {RECENT_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          }
        />
        <DataTable
          columns={recentColumns}
          data={sortedRecent}
          total={sortedRecent.length}
          isLoading={isLoading}
          onRowClick={handleRecentRowClick}
          emptyMessage="No recent imports"
          emptyDescription="Completed and failed imports will appear here."
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPaginationChange={setPageIndex}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </section>

      <JobDetailSheet
        jobId={detailJobId}
        onClose={() => setDetailJobId(null)}
      />
    </>
  );
}
